import React from 'react';

const DimensionResults = ({ dimensionScores, dimensionConsistency, totalQuestionsInDimension, filterDimension, traitCounts }) => {
  // Define readable dimension names and their paired values
  const dimensions = [
    {
      id: 'structureVsFlexibility',
      name: 'Terstruktur vs Fleksibel',
      left: 'Terstruktur',
      right: 'Fleksibel',
      leftTrait: 'structure',
      rightTrait: 'flexibility',
      descriptions: {
        left: 'Anda cenderung menyukai lingkungan yang terstruktur, terorganisir, dan dapat diprediksi. Anda menghargai rutinitas, rencana yang jelas, dan keteraturan.',
        neutral: 'Anda memiliki keseimbangan antara struktur dan fleksibilitas, dapat beradaptasi dengan berbagai situasi sesuai kebutuhan.',
        right: 'Anda cenderung menyukai lingkungan yang fleksibel dan spontan. Anda mudah beradaptasi dengan perubahan dan tidak terlalu bergantung pada rutinitas atau struktur yang kaku.'
      }
    },
    {
      id: 'introvertVsExtrovert',
      name: 'Introvert vs Ekstrovert',
      left: 'Introvert',
      right: 'Ekstrovert',
      leftTrait: 'introvert',
      rightTrait: 'extrovert',
      descriptions: {
        left: 'Anda cenderung mendapatkan energi dari waktu sendiri dan mungkin merasa lelah setelah banyak interaksi sosial. Anda menikmati refleksi internal dan pemikiran mendalam.',
        neutral: 'Anda memiliki keseimbangan yang baik antara sifat introvert dan ekstrovert, dapat beradaptasi dengan situasi sosial namun juga menikmati waktu sendiri.',
        right: 'Anda cenderung mendapatkan energi dari interaksi dengan orang lain dan menikmati lingkungan sosial yang ramai. Anda biasanya percaya diri dalam situasi sosial dan mudah membangun hubungan baru.'
      }
    },
    {
      id: 'practicalVsConceptual',
      name: 'Praktis vs Konseptual',
      left: 'Praktis',
      right: 'Konseptual',
      leftTrait: 'practical',
      rightTrait: 'conceptual',
      descriptions: {
        left: 'Anda cenderung berfokus pada hal-hal praktis dan konkret. Anda menyukai pendekatan langsung dan solusi yang telah terbukti efektif untuk masalah sehari-hari.',
        neutral: 'Anda dapat bekerja dengan baik menggunakan pendekatan praktis maupun konseptual, menyesuaikan dengan kebutuhan situasi.',
        right: 'Anda cenderung tertarik pada ide-ide abstrak dan pemikiran teoretis. Anda menikmati eksplorasi konsep baru dan inovasi untuk memecahkan masalah.'
      }
    },
    {
      id: 'riskVsCaution',
      name: 'Pengambil Risiko vs Kehati-hatian',
      left: 'Hati-hati',
      right: 'Pengambil Risiko',
      leftTrait: 'caution',
      rightTrait: 'risk',
      descriptions: {
        left: 'Anda cenderung berhati-hati dan metodis dalam pendekatan Anda. Anda menghargai stabilitas dan keamanan, serta lebih memilih untuk menghindari risiko yang tidak perlu.',
        neutral: 'Anda memiliki keseimbangan yang baik antara pengambilan risiko dan kehati-hatian, mampu mengevaluasi situasi dan bertindak sesuai.',
        right: 'Anda cenderung nyaman mengambil risiko dan mencoba hal-hal baru. Anda tidak takut dengan ketidakpastian dan melihat peluang di situasi yang menantang.'
      }
    },
    {
      id: 'independentVsCollaborative',
      name: 'Mandiri vs Kolaboratif',
      left: 'Mandiri',
      right: 'Kolaboratif',
      leftTrait: 'independent',
      rightTrait: 'collaborative',
      descriptions: {
        left: 'Anda cenderung suka bekerja secara mandiri dan mengambil keputusan sendiri. Anda percaya diri dengan kemampuan Anda dan menikmati otonomi dalam pekerjaan.',
        neutral: 'Anda dapat bekerja dengan baik secara mandiri maupun dalam tim, menyesuaikan gaya kerja berdasarkan tuntutan situasi.',
        right: 'Anda cenderung menikmati bekerja dengan orang lain dan menghargai kolaborasi. Anda meyakini bahwa diskusi kelompok dan kerjasama menghasilkan hasil terbaik.'
      }
    }
  ];

  // Filter dimensions if a specific one is requested
  const dimensionsToShow = filterDimension
    ? dimensions.filter(d => d.id === filterDimension)
    : dimensions;

  // Function to get color classes for score visualization
  const getScoreColor = (score, reversed) => {
    const baseScore = reversed ? 100 - score : score;
    if (baseScore >= 75) return 'bg-blue-500';
    if (baseScore >= 50) return 'bg-blue-400';
    if (baseScore >= 40) return 'bg-gray-400';
    if (baseScore >= 25) return 'bg-orange-400';
    return 'bg-orange-500';
  };

  // Function to get text color classes for score labels
  const getTextColor = (score, reversed) => {
    const baseScore = reversed ? 100 - score : score;
    if (baseScore >= 75) return 'text-blue-700 font-bold';
    if (baseScore >= 50) return 'text-blue-600';
    if (baseScore >= 40) return 'text-gray-600';
    if (baseScore >= 25) return 'text-orange-600';
    return 'text-orange-700 font-bold';
  };

  // Function to determine description based on score
  const getDescription = (dimension, score) => {
    if (score >= 66) return dimension.descriptions.left;
    if (score >= 33) return dimension.descriptions.neutral;
    return dimension.descriptions.right;
  };

  // Function to calculate normalized score using trait counts
  const calculateNormalizedScore = (dimension, traitCounts) => {
    // Jika traitCounts tidak tersedia, gunakan metode lama
    if (!traitCounts) {
      const score = dimensionScores[dimension.id] || 0;
      const maxPossible = totalQuestionsInDimension[dimension.id] || 10; // Default to 10 if not provided
      return Math.round((score / maxPossible) * 100);
    }
    
    // Gunakan traitCounts untuk menghitung skor yang lebih akurat
    const leftTraitCount = traitCounts[dimension.leftTrait] || 0;
    const rightTraitCount = traitCounts[dimension.rightTrait] || 0;
    const totalAnswers = leftTraitCount + rightTraitCount;
    
    if (totalAnswers === 0) return 50; // Default ke seimbang jika tidak ada jawaban
    
    // Hitung persentase trait kiri (skor lebih tinggi menunjukkan dominan trait kiri)
    return Math.round((leftTraitCount / totalAnswers) * 100);
  };
  
  // For a single dimension test, add more detailed explanation
  const getSingleDimensionExplanation = (dimension, score) => {
    const normalizedScore = calculateNormalizedScore(dimension, traitCounts);
    
    if (dimension.id === 'structureVsFlexibility') {
      if (normalizedScore >= 75) {
        return 'Anda sangat terstruktur dan metodis dalam pendekatan Anda. Dalam lingkungan kerja, Anda kemungkinan akan unggul dalam peran yang memerlukan keteraturan, detail, dan ketepatan. Anda mungkin merasa kesulitan dalam situasi yang sangat berubah-ubah atau tidak terstruktur.';
      } else if (normalizedScore >= 50) {
        return 'Anda cenderung terstruktur sambil mempertahankan kemampuan untuk beradaptasi. Anda mungkin lebih nyaman dengan rutinitas dan perencanaan, tetapi masih dapat menyesuaikan diri ketika diperlukan perubahan.';
      } else if (normalizedScore >= 25) {
        return 'Anda memiliki keseimbangan yang baik antara keteraturan dan spontanitas. Anda menghargai struktur untuk tujuan efisiensi, tetapi juga menikmati fleksibilitas untuk berimprovisasi dan menyesuaikan diri dengan situasi baru.';
      } else {
        return 'Anda sangat fleksibel dan adaptif dalam pendekatan Anda. Dalam lingkungan kerja, Anda kemungkinan akan unggul dalam peran yang membutuhkan improvisasi, berpikir cepat, dan penyesuaian terhadap situasi yang berubah. Anda mungkin merasa terbatas oleh aturan dan prosedur yang kaku.';
      }
    }
    
    else if (dimension.id === 'introvertVsExtrovert') {
      if (normalizedScore >= 75) {
        return 'Anda menunjukkan karakteristik yang sangat introvert. Anda cenderung merasa lebih nyaman bekerja sendiri dan memerlukan waktu untuk "mengisi ulang" setelah interaksi sosial yang intens. Kedalaman pemikiran Anda adalah kekuatan utama.';
      } else if (normalizedScore >= 50) {
        return 'Anda cenderung introvert tetapi memiliki beberapa karakteristik ekstrovert. Meskipun Anda membutuhkan waktu sendiri, Anda juga dapat berpartisipasi aktif dalam lingkungan sosial ketika diperlukan.';
      } else if (normalizedScore >= 25) {
        return 'Anda menunjukkan keseimbangan antara sifat introvert dan ekstrovert, yang sering disebut "ambivert". Anda dapat beradaptasi dengan baik dalam berbagai situasi sosial, menikmati interaksi maupun kesendirian.';
      } else {
        return 'Anda menunjukkan karakteristik yang sangat ekstrovert. Anda mendapatkan energi dari berinteraksi dengan orang lain dan cenderung menikmati lingkungan sosial yang ramai. Kemampuan networking dan komunikasi adalah kekuatan utama Anda.';
      }
    }
    
    else if (dimension.id === 'practicalVsConceptual') {
      if (normalizedScore >= 75) {
        return 'Anda sangat praktis dan berorientasi pada hasil. Anda fokus pada solusi konkret dan implementasi langsung, serta mungkin lebih menyukai pendekatan "hands-on" dalam bekerja.';
      } else if (normalizedScore >= 50) {
        return 'Anda cenderung praktis tetapi juga mengapresiasi konsep dan teori. Anda mampu menerjemahkan ide abstrak menjadi solusi nyata, menjembatani antara konsep dan aplikasi.';
      } else if (normalizedScore >= 25) {
        return 'Anda menunjukkan keseimbangan antara pemikiran praktis dan konseptual, mampu beralih antara keduanya sesuai kebutuhan situasi.';
      } else {
        return 'Anda sangat konseptual dalam pendekatan Anda. Anda menikmati pemikiran abstrak, melihat gambaran besar, dan mengeksplorasi kemungkinan baru. Inovasi dan kreativitas adalah kekuatan utama Anda.';
      }
    }
    
    else if (dimension.id === 'riskVsCaution') {
      if (normalizedScore >= 75) {
        return 'Anda sangat berhati-hati dalam pendekatan Anda. Anda menilai situasi secara menyeluruh sebelum bertindak dan lebih memilih keamanan daripada risiko. Anda mungkin unggul dalam peran yang membutuhkan ketelitian dan manajemen risiko.';
      } else if (normalizedScore >= 50) {
        return 'Anda cenderung berhati-hati tetapi masih bersedia mengambil risiko terukur. Anda biasanya mempertimbangkan konsekuensi sebelum bertindak, tetapi tidak terlalu dibatasi oleh ketakutan akan kegagalan.';
      } else if (normalizedScore >= 25) {
        return 'Anda seimbang antara kehati-hatian dan pengambilan risiko, mampu memilih pendekatan yang tepat berdasarkan situasi.';
      } else {
        return 'Anda sangat nyaman dengan pengambilan risiko. Anda tidak takut mencoba hal-hal baru dan melihat kegagalan sebagai kesempatan untuk belajar. Anda mungkin unggul dalam situasi yang membutuhkan inovasi dan keberanian.';
      }
    }
    
    else if (dimension.id === 'independentVsCollaborative') {
      if (normalizedScore >= 75) {
        return 'Anda sangat mandiri dalam pendekatan Anda. Anda percaya diri dengan kemampuan Anda dan menikmati bekerja secara otonom. Anda mungkin unggul dalam peran yang memungkinkan banyak kebebasan dan pengambilan keputusan independen.';
      } else if (normalizedScore >= 50) {
        return 'Anda cenderung mandiri tetapi juga menghargai masukan dari orang lain. Anda dapat bekerja sendiri dengan baik tetapi juga tidak keberatan bekerja sama saat diperlukan.';
      } else if (normalizedScore >= 25) {
        return 'Anda seimbang antara kemandirian dan kolaborasi, mampu beradaptasi dengan fleksibel antara bekerja sendiri dan bekerja dalam tim sesuai kebutuhan situasi.';
      } else {
        return 'Anda sangat kolaboratif dalam pendekatan Anda. Anda menikmati bekerja dengan orang lain dan percaya bahwa hasil terbaik muncul dari kerja sama tim. Keterampilan interpersonal dan kemampuan membangun konsensus adalah kekuatan utama Anda.';
      }
    }
    
    // Default explanation jika tidak ada yang spesifik
    return getDescription(dimension, normalizedScore);
  };

  return (
    <div className="space-y-8">
      {!filterDimension && (
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Profil Dimensi Kepribadian</h2>
      )}
      
      {dimensionsToShow.map(dimension => {
        const consistencyScore = Math.round(dimensionConsistency[dimension.id] || 0);
        const normalizedScore = calculateNormalizedScore(dimension, traitCounts);
        const description = filterDimension 
          ? getSingleDimensionExplanation(dimension, normalizedScore)
          : getDescription(dimension, normalizedScore);

        return (
          <div key={dimension.id} className="dimension-card">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-medium text-gray-800">{dimension.name}</h3>
              <div className="text-sm font-medium text-gray-600">
                Konsistensi: <span className={consistencyScore >= 75 ? 'text-green-600' : consistencyScore >= 60 ? 'text-yellow-600' : 'text-red-500'}>
                  {consistencyScore}%
                </span>
              </div>
            </div>
            
            {/* Menampilkan jumlah trait jika traitCounts tersedia */}
            {traitCounts && (
              <div className="text-xs text-gray-500 mb-3">
                {dimension.left}: {traitCounts[dimension.leftTrait] || 0} jawaban, 
                {dimension.right}: {traitCounts[dimension.rightTrait] || 0} jawaban
              </div>
            )}
            
            <div className="flex items-center mt-4 mb-2">
              <div className="w-1/3 text-left">
                <span className={getTextColor(normalizedScore, false)}>{dimension.left}</span>
              </div>
              <div className="w-1/3 text-center">
                <span className="text-gray-500 text-sm">Seimbang</span>
              </div>
              <div className="w-1/3 text-right">
                <span className={getTextColor(normalizedScore, true)}>{dimension.right}</span>
              </div>
            </div>
            
            <div className="h-3 w-full bg-gray-200 rounded-full mb-4">
              <div 
                className={`dimension-score-bar ${getScoreColor(normalizedScore, false)}`} 
                style={{ width: `${normalizedScore}%` }}
              ></div>
            </div>
            
            <p className="text-gray-700 mt-3">{description}</p>
            
            {/* Additional details for single dimension view */}
            {filterDimension && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Rekomendasi Pengembangan:</h4>
                <p className="text-blue-700">
                  {dimension.id === 'structureVsFlexibility' && 
                    (normalizedScore > 50
                      ? 'Cobalah beradaptasi dengan situasi tidak terencana. Latih kemampuan improvisasi Anda dengan mengambil proyek yang memerlukan kreativitas dan fleksibilitas.'
                      : 'Coba terapkan sedikit struktur dalam kegiatan Anda. Membuat jadwal dan menetapkan tujuan jangka pendek dapat membantu meningkatkan produktivitas Anda.')}
                  
                  {dimension.id === 'introvertVsExtrovert' && 
                    (normalizedScore > 50
                      ? 'Cobalah berpartisipasi dalam kegiatan kelompok kecil untuk membangun keterampilan sosial Anda secara bertahap. Tetapkan tujuan sederhana seperti memulai percakapan dengan satu orang baru setiap minggu.'
                      : 'Alokasikan waktu untuk refleksi diri dan pemulihan. Bahkan ekstrovert membutuhkan waktu untuk memproses pengalaman mereka dan merencanakan langkah berikutnya.')}
                  
                  {dimension.id === 'practicalVsConceptual' && 
                    (normalizedScore > 50
                      ? 'Cobalah mengeksplorasi konsep-konsep baru dan ide-ide abstrak. Membaca buku tentang teori atau filosofi dapat membantu memperluas pola pikir Anda.'
                      : 'Latih keterampilan praktis dan mulailah menerapkan ide-ide Anda dalam proyek nyata. Carilah contoh konkret dari konsep yang Anda kembangkan.')}
                  
                  {dimension.id === 'riskVsCaution' && 
                    (normalizedScore > 50
                      ? 'Tetapkan tujuan kecil untuk memulai pengambilan risiko yang terukur. Mulailah dengan keputusan kecil di mana konsekuensinya terbatas namun dapat memberikan pengalaman baru.'
                      : 'Kembangkan proses evaluasi risiko untuk menilai situasi secara objektif. Tidak semua risiko sama, dan beberapa mungkin lebih berharga diambil daripada yang lain.')}
                  
                  {dimension.id === 'independentVsCollaborative' && 
                    (normalizedScore > 50
                      ? 'Cobalah berpartisipasi dalam proyek tim kecil di mana Anda dapat berbagi perspektif dan belajar dari orang lain. Menerima masukan bisa menjadi kekuatan, bukan kelemahan.'
                      : 'Tetapkan waktu untuk bekerja secara mandiri pada proyek atau hobi. Mengembangkan kemandirian dan kepercayaan diri dalam pengambilan keputusan adalah keterampilan berharga.')}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DimensionResults;